// Cross-platform Band Sync wire protocol — TypeScript / Zod side.
//
// AUTHORITATIVE SOURCE: /Users/matthewbrown/sheetmuse-ios/band-sync-ios.md §5.
// AUTHORITATIVE SCHEMA: ../schema/band-sync.schema.json (the lockfile).
//
// This file mirrors §5 of the iOS doc exactly. The Architect agent owns it;
// builders treat it as read-only. A divergence between this file and
// band-sync-ios.md §5 is a P0 bug.
//
// Time domain: server-monotonic nanoseconds since the per-session `bandEpoch`.
// Wire format: bigint nanoseconds are sent as decimal strings (JSON cannot
// hold a 64-bit integer); we coerce to/from `bigint` at the parse boundary.

import { z } from "zod";

export const PROTOCOL_VERSION = 1 as const;

// ---------- primitive helpers ----------

const NsString = z
  .string()
  .regex(/^-?\d+$/, "ns must be a decimal-string integer")
  .transform((s) => BigInt(s));

const Ulid = z
  .string()
  .length(26)
  .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, "ULID");

const NonNegativeMs = z.number().nonnegative().finite();
const PositiveMs = z.number().positive().finite();
const Cents = z.number().finite().min(-2400).max(2400);
const Rate = z.number().finite().min(0.25).max(4);
const PositionSec = z.number().nonnegative().finite();

// ---------- envelope ----------

export const EnvelopeSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  op: z.string(),
  id: Ulid,
  from: z.string().min(1).max(64),
  to: z.union([z.literal("*"), z.string().min(1).max(64)]),
  ts: NsString,
});

export type Envelope = z.infer<typeof EnvelopeSchema>;

// ---------- session ops ----------

const RouteSchema = z.union([
  z.literal("internalSpeaker"),
  z.literal("wiredHeadphones"),
  z.literal("bluetoothA2DP"),
  z.literal("airplay"),
  z.literal("usb"),
  z.literal("unknown"),
]);

const AudioSessionInfo = z.object({
  sampleRate: z.number().int().positive(),
  ioBufferMs: z.number().positive().finite(),
  route: RouteSchema,
});

export const SessionHelloSchema = EnvelopeSchema.extend({
  op: z.literal("session.hello"),
  payload: z.object({
    bandId: z.string().min(1),
    claimedRole: z.union([z.literal("conductor"), z.literal("follower")]),
    deviceClass: z.string().min(1),
    appVersion: z.string().min(1),
    audioSession: AudioSessionInfo,
  }),
});

const MemberSchema = z.object({
  memberId: z.string().min(1),
  role: z.union([z.literal("conductor"), z.literal("follower")]),
  deviceClass: z.string(),
  joinedAtServerEpochNs: NsString,
});

const TransportSnapshotSchema = z.object({
  state: z.union([z.literal("playing"), z.literal("paused"), z.literal("stopped")]),
  songId: z.string().min(1).optional(),
  fromPositionSec: PositionSec,
  rate: Rate,
  pitchCents: Cents,
  serverActivationTimeNs: NsString,
});

export const SessionWelcomeSchema = EnvelopeSchema.extend({
  op: z.literal("session.welcome"),
  payload: z.object({
    memberId: z.string().min(1),
    bandEpochMs: NonNegativeMs, // debug only — never used for sync arithmetic
    serverMonotonicNowNs: NsString,
    members: z.array(MemberSchema),
    role: z.union([z.literal("conductor"), z.literal("follower")]),
    songId: z.string().min(1).optional(),
    transportSnapshot: TransportSnapshotSchema.optional(),
  }),
});

export const SessionMembersSchema = EnvelopeSchema.extend({
  op: z.literal("session.members"),
  payload: z.object({
    joined: MemberSchema.optional(),
    left: z.string().min(1).optional(),
    full: z.array(MemberSchema),
  }),
});

// ---------- time ops ----------

export const TimePingSchema = EnvelopeSchema.extend({
  op: z.literal("time.ping"),
  payload: z.object({
    t0: NsString, // client-monotonic ns at send
  }),
});

export const TimePongSchema = EnvelopeSchema.extend({
  op: z.literal("time.pong"),
  payload: z.object({
    t0: NsString, // echoed
    t1: NsString, // server-monotonic ns at receipt
    t2: NsString, // server-monotonic ns at reply
    serverMonoNowNs: NsString,
  }),
});

// ---------- clock ops ----------

export const ClockReportSchema = EnvelopeSchema.extend({
  op: z.literal("clock.report"),
  payload: z.object({
    songId: z.string().min(1),
    expectedPositionSec: PositionSec,
    actualPositionSec: PositionSec,
    driftMs: z.number().finite(),
    outputLatencyMs: NonNegativeMs,
    route: RouteSchema,
    rttMs: PositiveMs,
  }),
});

export const ClockMicroTrimSchema = EnvelopeSchema.extend({
  op: z.literal("clock.microTrim"),
  payload: z.object({
    deltaMs: z.number().finite(),
    durationMs: PositiveMs,
  }),
});

export const ClockSoftReScheduleSchema = EnvelopeSchema.extend({
  op: z.literal("clock.softReSchedule"),
  payload: z.object({
    targetPositionSec: PositionSec,
    serverActivationTimeNs: NsString,
  }),
});

export const ClockHardSyncSchema = EnvelopeSchema.extend({
  op: z.literal("clock.hardSync"),
  payload: z.object({
    targetPositionSec: PositionSec,
    serverActivationTimeNs: NsString,
  }),
});

// ---------- transport ops ----------

export const TransportPlaySchema = EnvelopeSchema.extend({
  op: z.literal("transport.play"),
  payload: z.object({
    songId: z.string().min(1),
    fromPositionSec: PositionSec,
    rate: Rate,
    pitchCents: Cents,
    serverActivationTimeNs: NsString,
  }),
});

export const TransportPauseSchema = EnvelopeSchema.extend({
  op: z.literal("transport.pause"),
  payload: z.object({
    atPositionSec: PositionSec,
    serverActivationTimeNs: NsString,
  }),
});

export const TransportSeekSchema = EnvelopeSchema.extend({
  op: z.literal("transport.seek"),
  payload: z.object({
    toPositionSec: PositionSec,
    isPlaying: z.boolean(),
    serverActivationTimeNs: NsString,
  }),
});

export const TransportSetRateSchema = EnvelopeSchema.extend({
  op: z.literal("transport.setRate"),
  payload: z.object({
    rate: Rate,
    serverActivationTimeNs: NsString,
  }),
});

export const TransportSetPitchSchema = EnvelopeSchema.extend({
  op: z.literal("transport.setPitch"),
  payload: z.object({
    pitchCents: Cents,
    serverActivationTimeNs: NsString,
  }),
});

export const TransportSetSongSchema = EnvelopeSchema.extend({
  op: z.literal("transport.setSong"),
  payload: z.object({
    songId: z.string().min(1),
    version: z.number().int().nonnegative(),
    manifestUrl: z.string().url(),
  }),
});

// ---------- system ops ----------

export const SystemAckSchema = EnvelopeSchema.extend({
  op: z.literal("system.ack"),
  payload: z.object({ refId: Ulid }),
});

export const SystemNackSchema = EnvelopeSchema.extend({
  op: z.literal("system.nack"),
  payload: z.object({ refId: Ulid, reason: z.string().min(1) }),
});

export const SystemErrorSchema = EnvelopeSchema.extend({
  op: z.literal("system.error"),
  payload: z.object({ code: z.string().min(1), message: z.string() }),
});

// ---------- discriminated unions ----------

export const ClientMessageSchema = z.discriminatedUnion("op", [
  SessionHelloSchema,
  TimePingSchema,
  ClockReportSchema,
  TransportPlaySchema,
  TransportPauseSchema,
  TransportSeekSchema,
  TransportSetRateSchema,
  TransportSetPitchSchema,
  TransportSetSongSchema,
  SystemAckSchema,
  SystemNackSchema,
]);

export const ServerMessageSchema = z.discriminatedUnion("op", [
  SessionWelcomeSchema,
  SessionMembersSchema,
  TimePongSchema,
  ClockMicroTrimSchema,
  ClockSoftReScheduleSchema,
  ClockHardSyncSchema,
  TransportPlaySchema,
  TransportPauseSchema,
  TransportSeekSchema,
  TransportSetRateSchema,
  TransportSetPitchSchema,
  TransportSetSongSchema,
  SystemErrorSchema,
]);

// ---------- exported types ----------

export type SessionHello = z.infer<typeof SessionHelloSchema>;
export type SessionWelcome = z.infer<typeof SessionWelcomeSchema>;
export type SessionMembers = z.infer<typeof SessionMembersSchema>;
export type TimePing = z.infer<typeof TimePingSchema>;
export type TimePong = z.infer<typeof TimePongSchema>;
export type ClockReport = z.infer<typeof ClockReportSchema>;
export type ClockMicroTrim = z.infer<typeof ClockMicroTrimSchema>;
export type ClockSoftReSchedule = z.infer<typeof ClockSoftReScheduleSchema>;
export type ClockHardSync = z.infer<typeof ClockHardSyncSchema>;
export type TransportPlay = z.infer<typeof TransportPlaySchema>;
export type TransportPause = z.infer<typeof TransportPauseSchema>;
export type TransportSeek = z.infer<typeof TransportSeekSchema>;
export type TransportSetRate = z.infer<typeof TransportSetRateSchema>;
export type TransportSetPitch = z.infer<typeof TransportSetPitchSchema>;
export type TransportSetSong = z.infer<typeof TransportSetSongSchema>;
export type SystemAck = z.infer<typeof SystemAckSchema>;
export type SystemNack = z.infer<typeof SystemNackSchema>;
export type SystemError = z.infer<typeof SystemErrorSchema>;

export type ClientToServerMessage = z.infer<typeof ClientMessageSchema>;
export type ServerToClientMessage = z.infer<typeof ServerMessageSchema>;

// ---------- parse helpers ----------

import { err, ok, type Result } from "@repo/lib/result";

export interface ParseError {
  readonly kind: "parseError";
  readonly issues: readonly string[];
}

export function parseClientMessage(input: unknown): Result<ClientToServerMessage, ParseError> {
  const r = ClientMessageSchema.safeParse(input);
  return r.success
    ? ok(r.data)
    : err({
        kind: "parseError",
        issues: r.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`),
      });
}

export function parseServerMessage(input: unknown): Result<ServerToClientMessage, ParseError> {
  const r = ServerMessageSchema.safeParse(input);
  return r.success
    ? ok(r.data)
    : err({
        kind: "parseError",
        issues: r.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`),
      });
}

/**
 * Encode a message for the wire. bigint fields are stringified as decimals,
 * matching the parser. JSON.stringify on a bigint throws; we walk the tree.
 */
export function encodeMessage(msg: ClientToServerMessage | ServerToClientMessage): string {
  return JSON.stringify(msg, (_key, value: unknown) =>
    typeof value === "bigint" ? value.toString() : value,
  );
}
